import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { loadStripe } from "@stripe/stripe-js";
import ParentHeader from "../../Parent/ParentHeader/ParentHeader";
import { Msr, useMaterialSymbols } from "./components";
import { getOireachtasEvent, getBalance } from "./api";
import { dueLabel } from "./instalments";
import { serviceConsumer } from "../../network/ServiceConsumer";
import { money } from "./quoteRows";
import { formatDate } from "../../helper/formatDate";
import "./oireachtas.css";
import "./t8.css";

// T8 — Registration complete, per the design mockup (8-confirmation.html).
// Stripe redirects here after confirmPayment (?payment_intent=...&
// redirect_status=...); the webhook writes the entries and emails the full
// receipt. This page is confirmation + next steps: the paid total is read
// back from the PaymentIntent (client secret is in the redirect URL) — the
// itemized receipt lives in the email until a receipt-lookup endpoint exists.

const BASE = process.env.REACT_APP_BASE_URL;

export default function Confirmation() {
  useMaterialSymbols();
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = React.useState(null);
  const [school, setSchool] = React.useState(null);
  const [pi, setPi] = React.useState(null);
  // "Pay in instalments" (card 15862648): what the school still owes.
  const [owing, setOwing] = React.useState(null);

  const params = new URLSearchParams(window.location.search);
  const status = params.get("redirect_status");
  const clientSecret = params.get("payment_intent_client_secret");
  const failed = status && status !== "succeeded";
  // full | firstInstalment | balance — set on the return_url by the payment page.
  const kind = params.get("kind") || "full";
  const isBalance = kind === "balance";
  // Balance left AFTER this payment, as the server computed it when the
  // payment was created (the webhook may not have recorded it yet).
  const afterParam = params.get("after");
  const afterCents = afterParam != null && /^\d+$/.test(afterParam) ? Number(afterParam) : null;
  const dueParam = params.get("due");

  React.useEffect(() => {
    document.title = isBalance ? "Feis.link - Payment received" : "Feis.link - Registration complete";
    let alive = true;
    getOireachtasEvent(id).then((ev) => { if (alive) setEvent(ev); }).catch(() => {});
    if (afterCents != null) {
      setOwing(afterCents > 0 ? { balanceCents: afterCents, balanceDueAt: dueParam } : null);
    } else if (kind === "full") {
      // A pay-in-full sitting can still sit beside an older open plan.
      getBalance(id)
        .then((orders) => {
          if (!alive) return;
          const open = (orders || []).filter((o) => o.balanceCents > 0);
          if (open.length) {
            setOwing({
              balanceCents: open.reduce((n, o) => n + Math.max(0, o.balanceCents - (o.pendingCents || 0)), 0),
              balanceDueAt: open[0].balanceDueAt,
            });
          }
        })
        .catch(() => {});
    }
    serviceConsumer("GET", `${BASE}/oireachtas/${id}/roster`)
      .then((r) => { if (alive && r.roster && r.roster.length) setSchool(r.roster[0].schoolName); })
      .catch(() => {});
    return () => { alive = false; };
    // The URL params are fixed for this page's lifetime (Stripe's redirect).
  }, [id, afterCents, dueParam, isBalance, kind]);

  // Read the paid amount + date back from Stripe (presentation only).
  React.useEffect(() => {
    if (!clientSecret) return;
    let alive = true;
    loadStripe(process.env.REACT_APP_PUBLISHKEY)
      .then((s) => s && s.retrievePaymentIntent(clientSecret))
      .then((res) => { if (alive && res && res.paymentIntent) setPi(res.paymentIntent); })
      .catch(() => {});
    return () => { alive = false; };
  }, [clientSecret]);

  const close = event && event.entriesCloseDate
    ? moment(formatDate(event.entriesCloseDate)).format("MMM D")
    : null;
  const paidDate = (pi && pi.created ? moment.unix(pi.created) : moment()).format("MMMM D, YYYY");

  return (
    <div className="oir">
      <ParentHeader />
      <div className="ocfm">
        <div className="hero">
          <div className="check">
            <span className="ring"><Msr>{failed ? "hourglass_top" : "check"}</Msr></span>
          </div>
          <h1>{failed ? "Payment processing" : isBalance ? "Payment received" : "Registration complete"}</h1>
          <p className="lead">
            {failed
              ? isBalance
                ? "Your payment has not been confirmed yet. It will be applied to your balance once it completes."
                : "Your payment has not been confirmed yet. Your entries will appear once it completes."
              : isBalance
                ? "Thank you — your payment has been applied to your school's balance."
                : "Payment received — thank you."}
          </p>
          {!failed && (
            <span className="emailed">
              <Msr>mark_email_read</Msr>A receipt has been emailed to you.
            </span>
          )}
        </div>

        {!failed && (
          <div className="rcpt">
            <div className="rcpt-head">
              <div>
                <div className="rn">Receipt</div>
                <div className="rd">{paidDate}</div>
              </div>
              <span className="paid"><Msr>check_circle</Msr>Paid</span>
            </div>

            <div className="rcpt-meta">
              <div className="m">
                <div className="l">Event</div>
                <div className="v"><Msr>emoji_events</Msr>{(event && event.name) || "—"}</div>
              </div>
              <div className="m">
                <div className="l">School</div>
                <div className="v"><Msr>school</Msr>{school || "—"}</div>
              </div>
              <div className="m">
                <div className="l">Payment</div>
                <div className="v"><Msr>credit_card</Msr>Card payment</div>
              </div>
              <div className="m">
                <div className="l">Status</div>
                <div className="v"><Msr>verified</Msr>Confirmed</div>
              </div>
            </div>

            {pi && pi.amount != null && (
              <div className="rcpt-body">
                <div className="rrow total">
                  <span className="k">{kind === "full" ? "Total paid" : "Amount paid"}</span>
                  <span className="v">{money(pi.amount)}</span>
                </div>
                {owing && (
                  <div className="rrow">
                    <span className="k">Balance owing{owing.balanceDueAt ? ` · due ${dueLabel(owing.balanceDueAt)}` : ""}</span>
                    <span className="v">{money(owing.balanceCents)}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="actions">
          <div className="secondary-row">
            <button type="button" className="bigbtn" onClick={() => navigate(`/teacher/oireachtas/${id}/entries`)}>
              <Msr>visibility</Msr>View current entries
            </button>
          </div>
        </div>

        <div className="footnote">
          <Msr>info</Msr>
          <span>
            {owing
              ? "Pay the balance any time from Current entries."
              : <>You can still add entries until the deadline{close ? ` (${close})` : ""}.</>}
          </span>
        </div>
      </div>
    </div>
  );
}
